import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Container,
  Grid,
} from "@mui/material";

const BingoMomentsComponent = () => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  const moments = [
    {
      id: 1,
      emoji: "🧑‍💻",
      title: "Employee Engagement",
      description: "Break the routine with a quick, fun activity",
      color: "black",
      gradient: "linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)",
    },
    {
      id: 2,
      emoji: "🎤",
      title: "Conferences & Events",
      description: "Energize attendees and boost interaction",
      color: "black",
      gradient: "linear-gradient(135deg, #4ECDC4 0%, #6EE7DE 100%)",
    },
    {
      id: 3,
      emoji: "🏆",
      title: "Rewards & Recognition",
      description: "Celebrate milestones in a playful way",
      color: "black",
      gradient: "linear-gradient(135deg, #45B7D1 0%, #67C8E2 100%)",
    },
    {
      id: 4,
      emoji: "🧊",
      title: "Icebreakers for New Joinees",
      description: "Help new employees warm up fast",
      color: "black",
      gradient: "linear-gradient(135deg, #96CEB4 0%, #B8E6C8 100%)",
    },
    {
      id: 5,
      emoji: "💬",
      title: "Culture Activation",
      description:
        "Reinforce values, DEI, or wellness themes with themed bingo",
      color: "black",
      gradient: "linear-gradient(135deg, #FFEAA7 0%, #FFF2C7 100%)",
    },
  ];

  const handleCardClick = (cardId) => {
    setSelectedCard(selectedCard === cardId ? null : cardId);
  };

  const handleCardHover = (cardId) => {
    setHoveredCard(cardId);
  };

  const handleCardLeave = () => {
    setHoveredCard(null);
  };

  // Keyframes for animations
  const styles = `
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-20px); }
    }
    
    @keyframes swing {
      0%, 100% { transform: rotate(0deg); }
      25% { transform: rotate(-2deg); }
      75% { transform: rotate(2deg); }
    }
    
    @keyframes hangingSwing {
      0%, 100% { transform: rotate(0deg) translateY(0px); }
      25% { transform: rotate(-3deg) translateY(-5px); }
      75% { transform: rotate(3deg) translateY(-5px); }
    }
    
    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.1); }
      100% { transform: scale(1); }
    }
    
    @keyframes slideIn {
      0% { opacity: 0; transform: translateY(10px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes cardHover {
      0% { transform: translateY(-8px) scale(1.02) rotate(0deg); }
      50% { transform: translateY(-12px) scale(1.03) rotate(1deg); }
      100% { transform: translateY(-8px) scale(1.02) rotate(0deg); }
    }
    
    @keyframes stringWave {
      0%, 100% { transform: scaleY(1); }
      50% { transform: scaleY(1.1); }
    }
    
    .hanging-card {
      animation: hangingSwing 6s ease-in-out infinite;
      transform-origin: top center;
    }
    
    .hanging-card:nth-child(1) { animation-delay: 0s; }
    .hanging-card:nth-child(2) { animation-delay: 1s; }
    .hanging-card:nth-child(3) { animation-delay: 2s; }
    .hanging-card:nth-child(4) { animation-delay: 3s; }
    .hanging-card:nth-child(5) { animation-delay: 4s; }
    
    .card-hover {
      animation: cardHover 0.8s ease-in-out;
    }
    
    .hanging-string {
      animation: stringWave 4s ease-in-out infinite;
    }
  `;

  return (
    <Box
      sx={{
        background:
          "linear-gradient(135deg,rgb(255, 255, 255) 0%,rgb(255, 255, 255) 100%)",
        minHeight: "100vh",
        padding: "40px 20px",
        position: "relative",
        overflow: "hidden",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      <style>{styles}</style>

      {/* Animated Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          left: "10%",
          width: "100px",
          height: "100px",
          borderRadius: "50%",
          background: "rgba(0, 0, 0, 0.05)",
          animation: "float 6s ease-in-out infinite",
          animationDelay: "0s",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: "20%",
          right: "15%",
          width: "60px",
          height: "60px",
          borderRadius: "50%",
          background: "rgba(0, 0, 0, 0.05)",
          animation: "float 6s ease-in-out infinite",
          animationDelay: "2s",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          bottom: "30%",
          left: "20%",
          width: "80px",
          height: "80px",
          borderRadius: "50%",
          background: "rgba(0, 0, 0, 0.05)",
          animation: "float 6s ease-in-out infinite",
          animationDelay: "4s",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", marginBottom: "80px" }}>
          <Typography
            variant="h5"
            sx={{
              color: "black",
              fontWeight: 900,
              lineHeight: 1.2,
              mb: 1,
              fontSize: { xs: "2rem", lg: "3rem" },
            }}
          >
            Built for Moments That Matter
          </Typography>
          <Typography>
            From casual breaks to big events — tailor your bingo game to your
            vibe.
          </Typography>
        </Box>

        {/* Hanging Cards Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr", // Single column on extra small screens
              sm: "repeat(auto-fit, minmax(280px, 1fr))", // Smaller min-width for small screens
              md: "repeat(auto-fit, minmax(300px, 1fr))" // Original size for medium and up
            },
            gap: { xs: "30px", sm: "40px" }, // Smaller gap on mobile
            maxWidth: "1200px",
            margin: "0 auto",
            position: "relative",
            zIndex: 1,
            paddingTop: "60px",
            justifyItems: "center", // Center grid items
          }}
        >
          {moments.map((moment, index) => (
            <Box
              key={moment.id}
              sx={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                width: "100%", // Ensure full width
                maxWidth: "320px", // Limit max width
              }}
            >
              {/* Hanging String */}
              <Box
                sx={{
                  position: "absolute",
                  top: "-60px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "3px",
                  height: "60px",
                  background: "linear-gradient(to bottom, #666, #999)",
                  borderRadius: "1.5px",
                  zIndex: 0,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
                className="hanging-string"
              />

              {/* Hanging Pin */}
              <Box
                sx={{
                  position: "absolute",
                  top: "-70px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "12px",
                  height: "12px",
                  background: "radial-gradient(circle, #FFD700, #FFA500)",
                  borderRadius: "50%",
                  border: "2px solid #fff",
                  zIndex: 1,
                  boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                }}
              />

              {/* Card */}
              <Card
                className={`hanging-card ${
                  hoveredCard === moment.id ? "card-hover" : ""
                }`}
                sx={{
                  background: moment.gradient,
                  borderRadius: "20px",
                  cursor: "pointer",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  transform:
                    hoveredCard === moment.id
                      ? "translateY(-15px) scale(1.05) rotate(2deg)"
                      : "translateY(0) scale(1) rotate(0deg)",
                  boxShadow:
                    hoveredCard === moment.id
                      ? "0 25px 50px rgba(0,0,0,0.25), 0 0 30px rgba(0,0,0,0.1)"
                      : selectedCard === moment.id
                      ? "0 20px 40px rgba(0,0,0,0.2), 0 0 20px rgba(0,0,0,0.1)"
                      : "0 10px 30px rgba(0,0,0,0.15)",
                  border:
                    selectedCard === moment.id
                      ? "3px solid #fff"
                      : "3px solid transparent",
                  position: "relative",
                  overflow: "visible",
                  zIndex: 2,
                  transformOrigin: "top center",
                  width: "100%",
                  maxWidth: "320px",
                }}
                onClick={() => handleCardClick(moment.id)}
                onMouseEnter={() => handleCardHover(moment.id)}
                onMouseLeave={handleCardLeave}
              >
                {selectedCard === moment.id && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "-15px",
                      right: "-15px",
                      background: "linear-gradient(45deg, #FFD700, #FFA500)",
                      borderRadius: "50%",
                      width: "35px",
                      height: "35px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "18px",
                      animation: "pulse 2s infinite",
                      boxShadow: "0 4px 15px rgba(255,215,0,0.4)",
                      border: "2px solid #fff",
                      zIndex: 10,
                    }}
                  >
                    ✨
                  </Box>
                )}

                <CardContent
                  sx={{
                    padding: { xs: "25px 20px", sm: "30px 25px" }, // Smaller padding on mobile
                    textAlign: "center",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      fontSize: { xs: "3rem", sm: "3.5rem" }, // Smaller emoji on mobile
                      marginBottom: "15px",
                      display: "block",
                      transition: "all 0.4s ease",
                      filter:
                        hoveredCard === moment.id
                          ? "drop-shadow(0 0 10px rgba(255,255,255,0.3))"
                          : "none",
                      transform:
                        hoveredCard === moment.id
                          ? "scale(1.3) rotate(10deg)"
                          : "scale(1)",
                      animation:
                        hoveredCard === moment.id
                          ? "pulse 1s infinite"
                          : "none",
                    }}
                  >
                    {moment.emoji}
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: { xs: "1.2rem", sm: "1.3rem" }, // Responsive font size
                      fontWeight: "bold",
                      color: "#fff",
                      marginBottom: "15px",
                      textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {moment.title}
                  </Typography>

                  <Typography
                    sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                      fontSize: { xs: "0.9rem", sm: "0.95rem" }, // Responsive font size
                      lineHeight: "1.4",
                      textShadow: "1px 1px 2px rgba(0,0,0,0.2)",
                    }}
                  >
                    {moment.description}
                  </Typography>

                  {selectedCard === moment.id && (
                    <Box
                      sx={{
                        marginTop: "20px",
                        animation: "slideIn 0.5s ease-out",
                      }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          background: "rgba(255, 255, 255, 0.2)",
                          color: "#fff",
                          borderRadius: "25px",
                          padding: { xs: "8px 20px", sm: "10px 25px" }, // Responsive padding
                          fontWeight: "bold",
                          backdropFilter: "blur(10px)",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          fontSize: { xs: "0.9rem", sm: "1rem" }, // Responsive font size
                          animation: "pulse 2s infinite",
                          "&:hover": {
                            background: "rgba(255, 255, 255, 0.3)",
                            transform: "scale(1.05)",
                          },
                        }}
                      >
                        Start Playing! 🎲
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default BingoMomentsComponent;